import { sanitizeUrl, sanitizeTextUrls } from '../../lib/sanitizeUrl';

interface Props {
  text: string;
  className?: string;
  large?: boolean;
}

interface ParsedBlock {
  type: 'heading' | 'bullet' | 'paragraph' | 'subject';
  level?: number;
  content: string;
}

function parseBlocks(raw: string): ParsedBlock[] {
  const cleaned = sanitizeTextUrls(raw);
  const lines = cleaned.split('\n');
  const blocks: ParsedBlock[] = [];
  let bulletBuffer: string[] = [];

  const flushBullets = () => {
    if (bulletBuffer.length > 0) {
      blocks.push({ type: 'bullet', content: bulletBuffer.join('\n') });
      bulletBuffer = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushBullets();
      continue;
    }

    const subjectMatch = trimmed.match(/^Subject:\s*(.+)/i);
    if (subjectMatch) {
      flushBullets();
      blocks.push({ type: 'subject', content: subjectMatch[1].trim() });
      continue;
    }

    const h2Match = trimmed.match(/^#{2}\s+(.+)/);
    if (h2Match) {
      flushBullets();
      blocks.push({ type: 'heading', level: 2, content: h2Match[1].replace(/[#*]+$/g, '').trim() });
      continue;
    }

    const h3Match = trimmed.match(/^#{3,}\s+(.+)/);
    if (h3Match) {
      flushBullets();
      blocks.push({ type: 'heading', level: 3, content: h3Match[1].replace(/[#*]+$/g, '').trim() });
      continue;
    }

    if (/^[A-Z][A-Z\s]{3,}$/.test(trimmed) && !trimmed.includes('  ')) {
      flushBullets();
      const cased = trimmed.charAt(0) + trimmed.slice(1).toLowerCase();
      blocks.push({ type: 'heading', level: 2, content: cased });
      continue;
    }

    if (/^[-*•]\s+/.test(trimmed) || /^\d+[.)]\s+/.test(trimmed)) {
      const bulletText = trimmed.replace(/^[-*•]\s+/, '').replace(/^\d+[.)]\s+/, '');
      bulletBuffer.push(bulletText);
      continue;
    }

    flushBullets();
    blocks.push({ type: 'paragraph', content: trimmed });
  }

  flushBullets();
  return blocks;
}

function renderInline(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  const regex = /(\*\*(.+?)\*\*)|(\[([^\]]+)\]\(([^)]+)\))|(`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyIdx = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      parts.push(<strong key={keyIdx++} className="font-semibold text-app-ink">{match[2]}</strong>);
    } else if (match[3]) {
      const href = sanitizeUrl(match[5]);
      parts.push(
        <a key={keyIdx++} href={href} target="_blank" rel="noopener noreferrer"
          className="text-forest-600 hover:text-forest-700 underline underline-offset-2">{match[4]}</a>
      );
    } else if (match[6]) {
      parts.push(<code key={keyIdx++} className="px-1 py-0.5 bg-app-canvas rounded text-[0.85em] font-mono">{match[7]}</code>);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

export default function FormattedText({ text, className = '', large }: Props) {
  if (!text) return null;

  const blocks = parseBlocks(text);
  const textSize = large ? 'text-base' : 'text-sm';
  const spacing = large ? 'space-y-5' : 'space-y-3';
  const bulletSpacing = large ? 'space-y-3' : 'space-y-2';
  const bulletDot = large ? 'mt-2.5 w-2 h-2' : 'mt-2 w-1.5 h-1.5';

  return (
    <div className={`${spacing} ${className}`}>
      {blocks.map((block, i) => {
        if (block.type === 'subject') {
          return (
            <div key={i} className="pb-2 mb-1 border-b border-app-line">
              <span className={`${textSize} font-bold text-app-ink`}>Subject: </span>
              <span className={`${textSize} text-app-ink`}>{renderInline(block.content)}</span>
            </div>
          );
        }

        if (block.type === 'heading' && block.level === 2) {
          return (
            <h4 key={i} className={`${large ? 'text-lg' : 'text-sm'} font-bold text-app-ink mt-4 first:mt-0`}>
              {renderInline(block.content)}
            </h4>
          );
        }

        if (block.type === 'heading' && block.level === 3) {
          return (
            <h5 key={i} className={`${textSize} font-semibold text-app-ink mt-3 first:mt-0`}>
              {renderInline(block.content)}
            </h5>
          );
        }

        if (block.type === 'bullet') {
          const items = block.content.split('\n');
          return (
            <ul key={i} className={`${bulletSpacing} pl-1`}>
              {items.map((item, j) => (
                <li key={j} className={`flex items-start gap-2.5 ${textSize} leading-relaxed`}>
                  <span className={`${bulletDot} rounded-full bg-app-muted flex-shrink-0`} />
                  <span>{renderInline(item)}</span>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={i} className={`${textSize} leading-relaxed`}>
            {renderInline(block.content)}
          </p>
        );
      })}
    </div>
  );
}
